import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Touch-Targets: CLAUDE.md §0 verlangt ≥44px auf Mobile. Wir erkennen echte
// Touch-Geraete via `@media (pointer: coarse)` und vergroessern dort die
// Button-Hoehe. Hover-Geraete (Desktop, Maus) bleiben kompakt.
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 focus-ring disabled:pointer-events-none disabled:opacity-50 gap-1.5 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-border-default bg-bg-surface text-text-primary hover:bg-bg-hover hover:border-border-strong",
        secondary:
          "bg-bg-subtle text-text-primary hover:bg-bg-hover",
        ghost: "text-text-secondary hover:bg-bg-hover hover:text-text-primary",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700",
        warning: "bg-amber-600 text-white shadow-sm hover:bg-amber-700",
      },
      size: {
        default:
          "h-9 px-4 py-2 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:px-5",
        sm: "h-8 rounded-md px-3 text-xs [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:px-4 [@media(pointer:coarse)]:text-sm",
        lg: "h-10 rounded-md px-6 [@media(pointer:coarse)]:h-12 [@media(pointer:coarse)]:px-7",
        icon:
          "h-9 w-9 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
